import { useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import "./index.css";

// ---------- PostHog ----------
const POSTHOG_KEY = "phc_D2uG3zHEabMBNicxo5ofykkk5YPseQiqZq8kaDGKBsQZ";
const POSTHOG_HOST = "https://us.i.posthog.com";

let posthogInitialized = false;
function ensurePosthog() {
  if (posthogInitialized) return;
  posthog.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, person_profiles: "identified_only" });
  posthogInitialized = true;
}

// ---------- Типы ----------
interface Diagnostics {
  userAgent: string;
  platform: string;
  isIOS: boolean;
  iosVersion: string;
  browser: string;
  screenSize: string;
  pixelRatio: number;
  visualViewport: string;
  guessedModel: string;
  gpu: string;
  deviceMemory: string;
  language: string;
  timezone: string;
  online: boolean;
  standalone: boolean;
  touchPoints: number;
  connectionType: string;
  downlink: string;
  ip: string;
  battery: string;
  sensors: string;
  mediaDevices: string;
  performanceScore: string;
  suspicious: string[];
}

interface Step {
  key: string;
  label: string;
}

// Расширенная таблица «разрешение + dpr → модель»
const SCREEN_TO_MODEL: Record<string, string> = {
  "375x667@2": "iPhone 6/6s/7/8 / SE (2/3)",
  "414x736@3": "iPhone 6/6s/7/8 Plus",
  "375x812@3": "iPhone X/XS/11 Pro / 12 mini / 13 mini",
  "414x896@2": "iPhone XR/11",
  "414x896@3": "iPhone XS Max / 11 Pro Max",
  "390x844@3": "iPhone 12/13/14",
  "428x926@3": "iPhone 12/13/14 Plus / 12/13 Pro Max",
  "393x852@3": "iPhone 14 Pro / 15 / 15 Pro",
  "430x932@3": "iPhone 14 Pro Max / 15 Plus / 15 Pro Max",
  "402x874@3": "iPhone 16 / 16 Pro",
  "440x956@3": "iPhone 16 Plus / 16 Pro Max",
  // запасные без dpr
  "375x667": "iPhone 6/7/8 / SE (2/3)",
  "414x736": "iPhone 6/7/8 Plus",
  "375x812": "iPhone X/XS/11 Pro/12 mini/13 mini",
  "414x896": "iPhone XR/11/XS Max/11 Pro Max",
  "390x844": "iPhone 12/13/14",
  "428x926": "iPhone 12/13/14 Plus/Pro Max",
  "393x852": "iPhone 14 Pro/15/15 Pro",
  "430x932": "iPhone 14 Pro Max/15 Plus/15 Pro Max",
  "402x874": "iPhone 16/16 Pro",
  "440x956": "iPhone 16 Plus/16 Pro Max",
};

const STEPS: Step[] = [
  { key: "device", label: "Модель, iOS и GPU" },
  { key: "display", label: "Экран и браузер" },
  { key: "sensors", label: "Батарея, датчики, камеры" },
  { key: "network", label: "Скорость и IP" },
  { key: "perf", label: "Производительность" },
  { key: "locale", label: "Язык и часовой пояс" },
];

const SPEED_TEST_POOL = [
  "https://speed.cloudflare.com/__down?bytes=10000000",
  "https://ash-speed.hetzner.com/10MB.bin",
  "https://speed.hetzner.de/10MB.bin",
  "https://proof.ovh.net/files/10Mb.dat",
];

function fetchWithTimeout(url: string, timeoutMs: number, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}

async function pickFastestUrl(urls: string[], timeoutMs = 2500): Promise<string | null> {
  const probes = urls.map((url) =>
    fetchWithTimeout(url, timeoutMs, { method: "HEAD", cache: "no-store" }).then(() => url),
  );
  try {
    return await Promise.any(probes);
  } catch {
    return null;
  }
}

async function measureDownloadSpeed(): Promise<string> {
  const url = await pickFastestUrl(SPEED_TEST_POOL);
  if (!url) return "не удалось измерить";

  try {
    const start = performance.now();
    const res = await fetchWithTimeout(url, 15000, { cache: "no-store" });
    const reader = res.body?.getReader();
    let bytes = 0;

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        bytes += value?.length ?? 0;
      }
    } else {
      bytes = (await res.arrayBuffer()).byteLength;
    }

    const seconds = (performance.now() - start) / 1000;
    if (bytes === 0 || seconds === 0) return "не удалось измерить";

    const mbps = (bytes * 8) / seconds / 1_000_000;
    return `${mbps.toFixed(1)} Мбит/с (${(bytes / 1_000_000).toFixed(1)} МБ · ${new URL(url).hostname})`;
  } catch {
    return "не удалось измерить";
  }
}

function getGPUInfo(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "WebGL недоступен";

    const ext = (gl as WebGLRenderingContext).getExtension("WEBGL_debug_renderer_info");
    if (ext) {
      const renderer = (gl as WebGLRenderingContext).getParameter(ext.UNMASKED_RENDERER_WEBGL);
      return String(renderer || "неизвестно");
    }
    return "renderer скрыт";
  } catch {
    return "ошибка WebGL";
  }
}

async function getBatteryInfo(): Promise<string> {
  try {
    const bat = await (navigator as any).getBattery?.();
    if (!bat) return "недоступно на iOS";
    const level = Math.round(bat.level * 100);
    const charging = bat.charging ? "заряжается" : "не заряжается";
    return `${level}% · ${charging}`;
  } catch {
    return "недоступно";
  }
}

function getSensorsInfo(): string {
  const hasMotion = typeof DeviceMotionEvent !== "undefined";
  const hasOrientation = typeof DeviceOrientationEvent !== "undefined";
  const parts: string[] = [];
  if (hasMotion) parts.push("акселерометр/гироскоп");
  if (hasOrientation) parts.push("ориентация");
  return parts.length ? parts.join(", ") : "датчики недоступны";
}

async function getMediaDevicesInfo(): Promise<string> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return "недоступно";
    const devices = await navigator.mediaDevices.enumerateDevices();
    const video = devices.filter((d) => d.kind === "videoinput").length;
    const audio = devices.filter((d) => d.kind === "audioinput").length;
    return `камер: ${video}, микрофонов: ${audio}`;
  } catch {
    return "доступ запрещён / недоступно";
  }
}

function runPerformanceTest(): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve("Canvas недоступен");
      return;
    }

    let frames = 0;
    const start = performance.now();
    const duration = 1200; // ~1.2 сек

    function loop(now: number) {
      if (now - start > duration) {
        const fps = Math.round((frames / (now - start)) * 1000);
        if (fps >= 50) resolve(`отлично (~${fps} fps)`);
        else if (fps >= 30) resolve(`норма (~${fps} fps)`);
        else resolve(`троттлинг / слабо (~${fps} fps)`);
        return;
      }
      // простая нагрузка
      for (let i = 0; i < 80; i++) {
        ctx.fillStyle = `hsl(${(frames + i) % 360},70%,50%)`;
        ctx.fillRect(Math.random() * 300, Math.random() * 300, 12, 12);
      }
      frames++;
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
  });
}

function detectSuspicious(): string[] {
  const issues: string[] = [];
  const ua = navigator.userAgent.toLowerCase();

  if ((window as any).webkit?.messageHandlers) {
    // нормально для Safari, но лишние handlers бывают при джейлбрейке
  }
  if (/cydia|sileo|zebra|unc0ver|checkra1n|taurine|dopamine/i.test(ua)) {
    issues.push("признаки jailbreak в UA");
  }
  if (!(window as any).SafariViewController && /iphone|ipad/.test(ua) && !/(version\/).*safari/i.test(ua)) {
    // слабый сигнал
  }
  if (window.location.protocol === "file:") {
    issues.push("открыто как file://");
  }
  return issues;
}

function guessModel(width: number, height: number, dpr: number): string {
  const keyWithDpr = `${width}x${height}@${Math.round(dpr)}`;
  const key = `${width}x${height}`;
  return SCREEN_TO_MODEL[keyWithDpr] || SCREEN_TO_MODEL[key] || "не определена";
}

function parseDiagnosticsBase(): Omit<Diagnostics, "downlink" | "ip" | "battery" | "mediaDevices" | "performanceScore"> {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);

  const iosMatch = ua.match(/OS (\d+)_(\d+)(?:_(\d+))?/);
  const iosVersion = iosMatch
    ? `${iosMatch[1]}.${iosMatch[2]}${iosMatch[3] ? "." + iosMatch[3] : ""}`
    : "не определено";

  const safariMatch = ua.match(/Version\/([\d.]+).*Safari/);
  const browser = safariMatch
    ? `Safari ${safariMatch[1]}`
    : /CriOS/.test(ua)
      ? "Chrome (iOS)"
      : /FxiOS/.test(ua)
        ? "Firefox (iOS)"
        : "неизвестный браузер";

  const w = window.screen.width;
  const h = window.screen.height;
  const dpr = window.devicePixelRatio || 1;
  const guessedModel = guessModel(w, h, dpr);

  const vv = window.visualViewport;
  const visualViewport = vv ? `${Math.round(vv.width)}×${Math.round(vv.height)}` : "н/д";

  const conn = (navigator as any).connection;
  const mem = (navigator as any).deviceMemory;

  return {
    userAgent: ua,
    platform: navigator.platform || "н/д",
    isIOS,
    iosVersion: isIOS ? iosVersion : "не iOS",
    browser,
    screenSize: `${w}×${h}`,
    pixelRatio: dpr,
    visualViewport,
    guessedModel,
    gpu: getGPUInfo(),
    deviceMemory: mem ? `${mem} ГБ` : "недоступно",
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
    standalone: window.matchMedia("(display-mode: standalone)").matches,
    touchPoints: navigator.maxTouchPoints ?? 0,
    connectionType: conn?.effectiveType ?? "не поддерживается iOS Safari",
    sensors: getSensorsInfo(),
    suspicious: detectSuspicious(),
  };
}

function isInAppBrowser(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const inAppPatterns = [
    "fban", "fbav", "instagram", "twitter", "line", "micro messenger",
    "wechat", "telegram", "whatsapp", "viber", "snapchat", "vk",
    "linkedin", "slack", "kakaotalk", "naver", "daum", "samsung",
  ];
  return inAppPatterns.some((p) => ua.includes(p));
}

function isTelegramMiniApp(): boolean {
  return typeof window !== "undefined" && !!(window as any).Telegram?.WebApp;
}

function parseTelegramInitData(initData: string): Record<string, any> {
  const params = new URLSearchParams(initData);
  const result: Record<string, any> = {};
  for (const [key, value] of params.entries()) {
    if (key === "user") {
      try {
        result.user = JSON.parse(decodeURIComponent(value));
      } catch {
        result.user = {};
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

function tryOpenInSafari() {
  try {
    const currentUrl = window.location.href;
    window.location.href = `x-safari-${currentUrl}`;
  } catch {
    console.log("Auto-open in Safari failed");
  }
}

type Stage = "intro" | "scanning" | "report" | "sent";

export function App() {
  const [stage, setStage] = useState<Stage>("intro");
  const [doneSteps, setDoneSteps] = useState<string[]>([]);
  const [diag, setDiag] = useState<Diagnostics | null>(null);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [isNotIOS, setIsNotIOS] = useState(false);
  const [showSafariPrompt, setShowSafariPrompt] = useState(false);
  const [isTelegram, setIsTelegram] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    ensurePosthog();
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const telegram = isTelegramMiniApp();
    setIsTelegram(telegram);

    if (telegram) {
      document.body.classList.add("telegram-theme");
      const tg = (window as any).Telegram.WebApp;
      tg.ready();
      tg.expand();

      const initData = tg.initData;
      if (initData) {
        const telegramData = parseTelegramInitData(initData);
        if (telegramData.user?.id) {
          posthog.identify(String(telegramData.user.id), {
            telegram_id: telegramData.user.id,
            telegram_first_name: telegramData.user.first_name,
            telegram_last_name: telegramData.user.last_name,
            telegram_username: telegramData.user.username,
            telegram_language_code: telegramData.user.language_code,
          });
        }
      }
    }

    if (!isIOS) {
      setIsNotIOS(true);
    } else if (isIOS && isInAppBrowser() && !telegram) {
      const hasSeenPrompt = localStorage.getItem("safari_prompt_seen");
      if (!hasSeenPrompt) {
        setShowSafariPrompt(true);
        tryOpenInSafari();
      }
    }

    if ((window as any).fpPromise) {
      (window as any).fpPromise
        .then((fp: any) => fp.get())
        .then((result: any) => {
          posthog.capture("fingerprint_collected", {
            visitor_id: result.visitor_id,
            event_id: result.event_id,
          });
        })
        .catch(() => {});
    }

    return () => timers.current.forEach(clearTimeout);
  }, []);

  const markDone = (key: string) =>
    setDoneSteps((prev) => (prev.includes(key) ? prev : [...prev, key]));

  const dismissSafariPrompt = () => {
    localStorage.setItem("safari_prompt_seen", "true");
    setShowSafariPrompt(false);
  };

  const runScan = async () => {
    setStage("scanning");
    setDoneSteps([]);
    const base = parseDiagnosticsBase();

    await new Promise((r) => setTimeout(r, 250));
    markDone("device");
    await new Promise((r) => setTimeout(r, 200));
    markDone("display");

    const [battery, mediaDevices] = await Promise.all([
      getBatteryInfo(),
      getMediaDevicesInfo(),
    ]);
    markDone("sensors");

    const [ipResult, downlink] = await Promise.all([
      fetch("https://api.ipify.org?format=json")
        .then((r) => r.json())
        .then((d) => d.ip ?? "недоступно")
        .catch(() => "недоступно"),
      measureDownloadSpeed(),
    ]);
    markDone("network");

    const performanceScore = await runPerformanceTest();
    markDone("perf");

    await new Promise((r) => setTimeout(r, 150));
    markDone("locale");

    const full: Diagnostics = {
      ...base,
      battery,
      mediaDevices,
      downlink,
      ip: ipResult,
      performanceScore,
    };

    setDiag(full);
    const t = setTimeout(() => setStage("report"), 300);
    timers.current.push(t);

    posthog.capture("device_scan_completed", full as any);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!diag) return;
    setSending(true);

    posthog.capture("device_application_submitted", { name, ...diag });

    if (isTelegram) {
      const tg = (window as any).Telegram.WebApp;
      const dataToSend = JSON.stringify({
        action: "test_completed",
        name,
        device_model: diag.guessedModel,
        ios_version: diag.iosVersion,
        browser: diag.browser,
        screen_size: diag.screenSize,
        pixel_ratio: diag.pixelRatio,
        visual_viewport: diag.visualViewport,
        gpu: diag.gpu,
        device_memory: diag.deviceMemory,
        battery: diag.battery,
        sensors: diag.sensors,
        media_devices: diag.mediaDevices,
        performance: diag.performanceScore,
        connection_type: diag.connectionType,
        downlink: diag.downlink,
        ip: diag.ip,
        language: diag.language,
        timezone: diag.timezone,
        suspicious: diag.suspicious,
      });
      tg.sendData(dataToSend);
      setTimeout(() => tg.close(), 500);
    } else {
      setTimeout(() => {
        setSending(false);
        setStage("sent");
      }, 500);
    }
  };

  return (
    <div className="page">
      <main className="panel">
        {stage === "intro" && (
          <section className="card intro">
            <div className="intro__icon" aria-hidden>
              📱
            </div>
            <h1>Проверим ваш iPhone</h1>
            <p className="intro__text">
              Соберём технические данные устройства — модель, iOS, GPU, батарею,
              датчики и производительность. Это займёт несколько секунд и поможет
              точнее оценить телефон.
            </p>
            <button className="btn btn--primary" onClick={runScan}>
              Начать проверку
            </button>
          </section>
        )}

        {stage === "scanning" && (
          <section className="card scanning" aria-live="polite">
            <div className="spinner" />
            <h2>Собираем данные…</h2>
            <ul className="checklist">
              {STEPS.map((step) => {
                const done = doneSteps.includes(step.key);
                return (
                  <li
                    key={step.key}
                    className={done ? "checklist__item checklist__item--done" : "checklist__item"}
                  >
                    <span className="checklist__mark">{done ? "✓" : ""}</span>
                    {step.label}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {stage === "report" && diag && (
          <section className="card report">
            <h2>Готово</h2>
            <p className="report__subtitle">Проверьте данные и укажите, как к вам обращаться</p>

            <div className="summary">
              <div className="summary__row">
                <span>Модель</span>
                <strong>{diag.guessedModel}</strong>
              </div>
              <div className="summary__row">
                <span>iOS</span>
                <strong>{diag.iosVersion}</strong>
              </div>
              <div className="summary__row">
                <span>GPU</span>
                <strong>{diag.gpu}</strong>
              </div>
              <div className="summary__row">
                <span>Экран</span>
                <strong>
                  {diag.screenSize} @{diag.pixelRatio}x
                </strong>
              </div>
              <div className="summary__row">
                <span>Батарея</span>
                <strong>{diag.battery}</strong>
              </div>
              <div className="summary__row">
                <span>Датчики</span>
                <strong>{diag.sensors}</strong>
              </div>
              <div className="summary__row">
                <span>Камеры / мик</span>
                <strong>{diag.mediaDevices}</strong>
              </div>
              <div className="summary__row">
                <span>Производительность</span>
                <strong>{diag.performanceScore}</strong>
              </div>
              <div className="summary__row">
                <span>Сеть</span>
                <strong>{diag.downlink}</strong>
              </div>
              <div className="summary__row">
                <span>IP</span>
                <strong>{diag.ip}</strong>
              </div>
              {diag.suspicious.length > 0 && (
                <div className="summary__row">
                  <span>Замечания</span>
                  <strong>{diag.suspicious.join(", ")}</strong>
                </div>
              )}
            </div>

            <form className="form" onSubmit={submit}>
              <label className="field">
                <span>Имя</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  name="name"
                  autoComplete="name"
                  placeholder="Как к вам обращаться"
                  required
                />
              </label>
              <button type="submit" className="btn btn--primary btn--wide" disabled={sending}>
                {sending ? "Отправляем…" : "Отправить заявку"}
              </button>
            </form>
          </section>
        )}

        {stage === "sent" && (
          <section className="card sent">
            <div className="sent__icon" aria-hidden>
              ✓
            </div>
            <h2>Спасибо{name ? `, ${name}` : ""}!</h2>
            <p>
              Заявка и данные устройства отправлены. Команда свяжется с вами в
              ближайшее время.
            </p>
          </section>
        )}
      </main>

      <small className="disclaimer">
        Начиная проверку, вы соглашаетесь на сбор технических данных устройства
        в целях оценки и связи с вами.
      </small>
    </div>
  );
}

export default App;